import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCamera } from '../contexts/CameraContext';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Upload, X, ArrowLeft, Settings } from 'lucide-react';
import { API_URL } from '../utils/api';

// Dev-only chatty logger for the camera flow. Silent in production builds.
// (These logs guard the iOS permission-loop invariant during development.)
const DEBUG_CAM = process.env.NODE_ENV !== 'production';
const dbg = DEBUG_CAM ? (...args) => console.log(...args) : () => {};

export default function Scan() {
  const [capturedImage, setCapturedImage] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [cameraFailed, setCameraFailed] = useState(false);
  const [showFlash, setShowFlash] = useState(false);
  const [mountId] = useState(() => Date.now()); // Track component instance
  
  const fileInputRef = useRef(null);
  const localVideoRef = useRef(null);
  
  const { token } = useAuth();
  const navigate = useNavigate();
  
  // Use global camera context - stream persists across route changes
  const { 
    permissionStatus, 
    isStreamReady,
    streamRef,
    initializeStream,
    attachStreamToVideo,
    capturePhotoFromVideo,
    hasActiveStream,
    PERMISSION_STATUS 
  } = useCamera();

  // Lock body scroll when camera view is open (fullscreen experience)
  useEffect(() => {
    // Add class to body to prevent scrolling
    document.body.classList.add('camera-open');
    dbg(`[Scan ${mountId}] Body scroll locked for fullscreen camera`);
    
    return () => {
      // Restore scrolling when leaving scan page
      document.body.classList.remove('camera-open');
      dbg(`[Scan ${mountId}] Body scroll restored`);
    };
  }, [mountId]);

  // Initialize camera when component mounts - only request stream if needed
  useEffect(() => {
    dbg(`[Scan ${mountId}] Component mounted`);
    let timeoutId = null;
    
    const setupCamera = async () => {
      // Skip if permission denied
      if (permissionStatus === PERMISSION_STATUS.DENIED) {
        dbg(`[Scan ${mountId}] Permission denied, skipping setup`);
        return;
      }

      // Check if we already have an active stream
      if (hasActiveStream()) {
        dbg(`[Scan ${mountId}] Active stream exists, reusing it`);
        
        // Just attach to our local video element
        if (localVideoRef.current) {
          const attached = attachStreamToVideo(localVideoRef.current);
          if (attached) {
            try {
              await localVideoRef.current.play();
              dbg(`[Scan ${mountId}] Video playing with existing stream`);
              setVideoReady(true);
            } catch (e) {
              dbg(`[Scan ${mountId}] Video play error:`, e.message);
            }
          }
        }
        return;
      }

      // No active stream - initialize one (this should only happen once per session)
      dbg(`[Scan ${mountId}] No active stream, initializing...`);
      
      // Set a timeout to show upload option if camera doesn't start within 10 seconds
      timeoutId = setTimeout(() => {
        if (!videoReady) {
          dbg(`[Scan ${mountId}] Camera initialization timeout - showing upload fallback`);
          setCameraFailed(true);
        }
      }, 10000);
      
      const stream = await initializeStream();
      
      if (stream && localVideoRef.current) {
        dbg(`[Scan ${mountId}] Got new stream, attaching to video`);
        localVideoRef.current.srcObject = stream;
        
        localVideoRef.current.onloadedmetadata = () => {
          localVideoRef.current.play()
            .then(() => {
              dbg(`[Scan ${mountId}] Video playing with new stream`);
              if (timeoutId) clearTimeout(timeoutId);
              setVideoReady(true);
            })
            .catch(e => dbg(`[Scan ${mountId}] Video play error:`, e.message));
        };
      } else if (!stream) {
        // Camera failed to initialize - show upload option
        dbg(`[Scan ${mountId}] Camera failed to initialize - showing upload fallback`);
        if (timeoutId) clearTimeout(timeoutId);
        setCameraFailed(true);
      }
    };

    setupCamera();

    // Cleanup: Do NOT stop the stream, just log unmount
    return () => {
      dbg(`[Scan ${mountId}] Component unmounting - stream stays alive`);
      if (timeoutId) clearTimeout(timeoutId);
      // Important: We do NOT stop the stream here
      // The stream is managed by CameraContext and should persist
    };
  }, [mountId, permissionStatus, initializeStream, attachStreamToVideo, hasActiveStream, PERMISSION_STATUS]);

  // Handle stream becoming ready (e.g., after navigation back to this page)
  useEffect(() => {
    if (isStreamReady && hasActiveStream() && localVideoRef.current && !videoReady) {
      dbg(`[Scan ${mountId}] Stream ready effect - attaching to video`);
      
      const attached = attachStreamToVideo(localVideoRef.current);
      if (attached && localVideoRef.current.paused) {
        localVideoRef.current.play()
          .then(() => setVideoReady(true))
          .catch(e => dbg(`[Scan ${mountId}] Play error:`, e.message));
      }
    }
  }, [mountId, isStreamReady, hasActiveStream, attachStreamToVideo, videoReady]);

  // Capture photo from current video frame
  const handleSubmit = useCallback(async (imageData) => {
    if (!imageData) {
      toast.error('Please capture or upload an image');
      return;
    }

    setAnalyzing(true);
    try {
      const base64Response = await fetch(imageData);
      const blob = await base64Response.blob();
      
      const formData = new FormData();
      formData.append('file', blob, 'scan.jpg');

      const response = await axios.post(
        `${API_URL}/scan`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Stash streak + milestone keyed by scan id so Result can show celebration
      try {
        const { id, streak_days, total_scans, milestone } = response.data || {};
        if (id) {
          sessionStorage.setItem(
            `qc:milestone:${id}`,
            JSON.stringify({ streak_days, total_scans, milestone })
          );
        }
      } catch (e) {
        // sessionStorage unavailable — celebration will be skipped. Not user-facing.
        console.warn('[Scan] sessionStorage.setItem failed, milestone celebration disabled:', e);
      }

      // Navigate to result - stream stays alive in context
      navigate(`/result/${response.data.id}`);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Scan limit reached');
        navigate('/upgrade');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to analyze image');
      }
      setAnalyzing(false);
      setCapturedImage(null);
    }
  }, [token, navigate]);

  // Trigger haptic feedback on supported devices (iOS/Android)
  const triggerHapticFeedback = useCallback(() => {
    // Try Vibration API first (widely supported)
    if ('vibrate' in navigator) {
      navigator.vibrate(50); // Short 50ms vibration
      dbg(`[Scan ${mountId}] Haptic feedback triggered via Vibration API`);
      return;
    }
    
    // Fallback: Try webkit haptic feedback for iOS Safari
    if (window.webkit?.messageHandlers?.haptic) {
      window.webkit.messageHandlers.haptic.postMessage('impact');
      dbg(`[Scan ${mountId}] Haptic feedback triggered via webkit`);
    }
  }, [mountId]);

  // Capture photo from current video frame
  const capturePhoto = useCallback(() => {
    if (!localVideoRef.current) {
      dbg(`[Scan ${mountId}] Video element not ready`);
      toast.error('Camera not ready');
      return;
    }

    // Trigger haptic feedback and flash effect for satisfying capture feel
    triggerHapticFeedback();
    setShowFlash(true);
    setTimeout(() => setShowFlash(false), 150);

    dbg(`[Scan ${mountId}] Capturing photo from video frame`);
    
    const imageData = capturePhotoFromVideo(localVideoRef.current);
    
    if (!imageData) {
      toast.error('Failed to capture photo');
      return;
    }

    dbg(`[Scan ${mountId}] Photo captured successfully`);
    setCapturedImage(imageData);
    handleSubmit(imageData);
  }, [mountId, capturePhotoFromVideo, handleSubmit, triggerHapticFeedback]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const imageData = reader.result;
        setCapturedImage(imageData);
        await handleSubmit(imageData);
      };
      reader.readAsDataURL(file);
    }
  };

  const retakePhoto = () => {
    dbg(`[Scan ${mountId}] Retaking photo - stream unchanged`);
    setCapturedImage(null);
    setAnalyzing(false);
  };

  const openSettings = () => {
    toast.info('Open your browser settings to enable camera access');
  };

  // Render camera denied or failed state (fullscreen layout)
  if (permissionStatus === PERMISSION_STATUS.DENIED || cameraFailed) {
    return (
      <div className="camera-fullscreen flex flex-col bg-qc-bg">
        {/* Header with safe area */}
        <div className="bg-qc-bg/80 backdrop-blur-lg px-5 py-3 flex items-center z-30 camera-header-safe">
          <Button
            onClick={() => navigate('/home')}
            variant="ghost"
            size="sm"
            data-testid="back-to-home-button"
            className="text-qc-text hover:bg-qc-surface h-10 w-10 p-0 rounded-full"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-base font-extrabold text-qc-text ml-3">Scan Product</h1>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-5">
          <div className="w-full max-w-md text-center">
            <div className="bg-qc-surface rounded-3xl p-8 border border-qc-border">
              <div className="w-20 h-20 bg-qc-surface2 rounded-full flex items-center justify-center mx-auto mb-5">
                <Camera className="w-10 h-10 text-qc-text-faint" />
              </div>
              <h2 className="text-2xl font-extrabold text-qc-text mb-2">
                {cameraFailed ? 'Camera Not Available' : 'Camera Access Disabled'}
              </h2>
              <p className="text-qc-text-dim text-base mb-6 leading-snug">
                {cameraFailed
                  ? 'Could not access camera. You can still upload photos to scan items.'
                  : 'To scan items, enable Camera in your device Settings. Or upload a photo instead.'}
              </p>

              {!cameraFailed && (
                <Button
                  onClick={openSettings}
                  variant="outline"
                  className="w-full mb-3 bg-qc-surface2 border-qc-border text-qc-text hover:bg-qc-border rounded-xl h-12 font-bold"
                >
                  <Settings className="w-5 h-5 mr-2" />
                  Open Settings
                </Button>
              )}

              <Button
                onClick={() => fileInputRef.current?.click()}
                className="w-full bg-qc-green hover:bg-qc-green-dark text-qc-bg rounded-xl h-12 font-extrabold"
                data-testid="upload-button-denied"
              >
                <Upload className="w-5 h-5 mr-2" />
                Upload Image Instead
              </Button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="file-input-denied"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="camera-fullscreen flex flex-col bg-black">
      {/* Header with safe area padding */}
      <div className="absolute top-0 left-0 right-0 px-5 py-3 flex items-center z-30 camera-header-safe bg-gradient-to-b from-black/60 via-black/30 to-transparent">
        <Button
          onClick={() => navigate('/home')}
          variant="ghost"
          size="sm"
          data-testid="back-to-home-button"
          className="text-qc-text hover:bg-white/10 h-10 w-10 p-0 rounded-full bg-black/40 backdrop-blur-sm"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-base font-extrabold text-qc-text ml-3 [text-shadow:0_1px_4px_rgba(0,0,0,0.6)]">Scan Product</h1>
      </div>

      {/* Camera Area - fills remaining space */}
      <div className="flex-1 flex flex-col relative overflow-hidden">

        {/* Live Camera View - shown when no captured image and not analyzing */}
        {!capturedImage && !analyzing && (
          <>
            {/* Full-screen video container */}
            <div className="flex-1 relative bg-black">
              {/* Video element - fills container */}
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className="absolute inset-0 w-full h-full object-cover"
              />

              {/* Loading overlay */}
              {!videoReady && (
                <div className="absolute inset-0 flex items-center justify-center bg-qc-bg">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-4 border-qc-green border-t-transparent mx-auto mb-3" />
                    <p className="text-qc-text-dim text-sm">Starting camera...</p>
                  </div>
                </div>
              )}

              {/* Scan guide overlay + animated scan line */}
              {videoReady && (
                <div className="absolute inset-0 pointer-events-none">
                  {/* Corner frame brackets */}
                  <div className="absolute top-[18%] bottom-[28%] left-6 right-6">
                    <span className="absolute top-0 left-0 w-8 h-8 border-t-[3px] border-l-[3px] border-qc-green rounded-tl-xl" />
                    <span className="absolute top-0 right-0 w-8 h-8 border-t-[3px] border-r-[3px] border-qc-green rounded-tr-xl" />
                    <span className="absolute bottom-0 left-0 w-8 h-8 border-b-[3px] border-l-[3px] border-qc-green rounded-bl-xl" />
                    <span className="absolute bottom-0 right-0 w-8 h-8 border-b-[3px] border-r-[3px] border-qc-green rounded-br-xl" />
                    {/* Animated scanning line */}
                    <div className="scan-line" data-testid="scan-line" />
                  </div>

                  {/* Hint text */}
                  <p
                    className="absolute left-0 right-0 text-center text-white/85 text-sm font-bold tracking-wide [text-shadow:0_1px_6px_rgba(0,0,0,0.7)]"
                    style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 11.5rem)' }}
                    data-testid="scan-hint"
                  >
                    Point at any product
                  </p>
                </div>
              )}

              {/* Flash effect on capture */}
              {showFlash && (
                <div
                  className="absolute inset-0 bg-white pointer-events-none"
                  style={{ animation: 'flash 150ms ease-out' }}
                />
              )}
            </div>

            {/* Fixed action buttons at bottom with safe area padding */}
            {videoReady && (
              <div className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-t from-black via-black/90 to-transparent pt-10 camera-buttons-safe">
                <div className="flex items-center justify-center gap-6 max-w-lg mx-auto">
                  {/* Upload secondary action */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="upload-image-button"
                    className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white flex items-center justify-center tappable"
                  >
                    <Upload className="w-6 h-6" />
                  </button>

                  {/* Large capture button - native style */}
                  <button
                    onClick={capturePhoto}
                    data-testid="capture-button"
                    aria-label="Capture"
                    className="capture-btn relative w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-[0_8px_30px_rgba(255,255,255,0.25)]"
                  >
                    <span className="absolute inset-0 rounded-full border-4 border-white" />
                    <span className="w-16 h-16 rounded-full bg-white border-4 border-black/0" />
                    <span className="absolute inset-1.5 rounded-full ring-2 ring-qc-green/60" />
                  </button>

                  {/* Spacer to balance layout */}
                  <div className="w-14 h-14" />
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="file-input"
            />
          </>
        )}

        {/* Captured Image Preview */}
        {capturedImage && !analyzing && (
          <div className="flex-1 flex flex-col">
            <div className="flex-1 relative">
              <img src={capturedImage} alt="Captured" className="absolute inset-0 w-full h-full object-contain bg-black" />
              <button
                onClick={retakePhoto}
                data-testid="remove-image-button"
                className="absolute top-4 right-4 w-10 h-10 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/80 transition-all z-10"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Fixed retake button at bottom */}
            <div className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-t from-black via-black/95 to-transparent pt-8 camera-buttons-safe">
              <div className="max-w-lg mx-auto">
                <Button
                  onClick={retakePhoto}
                  variant="outline"
                  className="w-full h-12 bg-qc-surface border-qc-border text-qc-text hover:bg-qc-border rounded-xl font-bold"
                >
                  Retake
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Analyzing State */}
        {analyzing && (
          <div className="flex-1 flex flex-col items-center justify-center px-5 bg-black">
            <div className="w-full max-w-sm text-center">
              <div className="relative rounded-3xl overflow-hidden mb-6 border border-qc-border">
                <img src={capturedImage} alt="Analyzing" className="w-full opacity-60" />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <div className="bg-black/40 backdrop-blur-md rounded-2xl p-6 border border-qc-border">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-qc-green border-t-transparent mx-auto mb-3" />
                    <p className="text-qc-text font-extrabold">Analyzing...</p>
                  </div>
                </div>
              </div>
              <p className="text-qc-text-dim text-sm">Getting your verdict</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
